import { useState, useEffect, useRef } from 'react';
import { safePostMessage } from '@utils/index';
import { PostMessageType } from '@constants/postMessage';

const ALLOWED_DOMAINS = (process.env.REACT_APP_ALLOWED_DOMAINS?.split(',') || []);

const isAllowedOrigin = (eventOrigin: string): boolean => {
    try {
        const origin = new URL(eventOrigin);
        const originDomain = origin.port
            ? `${origin.hostname}:${origin.port}`
            : origin.hostname;
        const isAllowed = ALLOWED_DOMAINS.some(
            (domain) =>
                originDomain === domain ||
                origin.hostname.endsWith(domain) ||
                (domain.startsWith('localhost') && origin.hostname === 'localhost')
        );
        if (!isAllowed) {
            console.log('[GeoLocation] Origin rejected:', eventOrigin, '(allowed:', ALLOWED_DOMAINS, ')');
        }
        return isAllowed;
    } catch {
        return false;
    }
};

const reverseGeocode = async (lat: number, lon: number): Promise<string> => {
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`,
            { headers: { 'Accept-Language': 'vi' } }
        );
        const data = await res.json();
        return (data.display_name as string) || '';
    } catch {
        return '';
    }
};

export const useGeoLocation = (): string => {
    const [location, setLocation] = useState('');
    const requestedRef = useRef(false);

    useEffect(() => {
        console.log('[GeoLocation] Mounted, inIframe:', window.self !== window.top, 'allowedDomains:', ALLOWED_DOMAINS);

        const requestLocation = () => {
            if (requestedRef.current) {
                console.log('[GeoLocation] Already requested, skip');
                return;
            }
            if (!navigator.geolocation) {
                console.log('[GeoLocation] geolocation not available');
                return;
            }
            requestedRef.current = true;
            console.log('[GeoLocation] Calling getCurrentPosition...');
            navigator.geolocation.getCurrentPosition(
                async ({ coords }) => {
                    console.log('[GeoLocation] ✅ Got coords:', coords.latitude, coords.longitude);
                    const address = await reverseGeocode(coords.latitude, coords.longitude);
                    console.log('[GeoLocation] ✅ Address:', address);
                    setLocation(address);
                },
                (err) => {
                    console.warn('[GeoLocation] ❌ Error code:', err.code, '|', err.message);
                    requestedRef.current = false;
                },
                { timeout: 10000 }
            );
        };

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === PostMessageType.CHAT_TOGGLE) {
                console.log('[GeoLocation] CHAT_TOGGLE received, isOpen:', event.data?.isOpen, 'from:', event.origin);
                if (event.data?.isOpen === true) {
                        requestLocation();
                    
                }
            }
        };

        globalThis.addEventListener('message', handleMessage);

        // Request location on mount (works in iframe with allow="geolocation" and standalone)
        console.log('[GeoLocation] Requesting location on mount');
        requestLocation();
        return () => {
            globalThis.removeEventListener('message', handleMessage);
        };
    }, []);

    return location;
};
